import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const TermsOfService = () => {
    return (
        <div className="min-h-screen bg-[#F9F6F0] pt-24 noise-overlay" data-testid="terms-of-service-page">
            <section className="section-padding">
                <div className="container-wide mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="max-w-4xl mx-auto"
                    >
                        <span className="inline-block px-4 py-1.5 bg-[#C8A86B]/10 text-[#C8A86B] text-sm font-medium tracking-widest-custom uppercase rounded-full mb-6">
                            Legal
                        </span>
                        <h1 className="font-heading text-4xl sm:text-5xl text-[#4A4E4D] mb-8">
                            Terms of Service
                        </h1>
                        <p className="text-[#4A4E4D]/60 mb-8">
                            Last updated: February 17, 2026
                        </p>

                        <div className="prose prose-lg max-w-none space-y-8">
                            <section className="bg-white rounded-2xl p-8 border border-[#E0DAD0]">
                                <h2 className="font-heading text-2xl text-[#4A4E4D] mb-4">1. Agreement to Terms</h2>
                                <p className="text-[#4A4E4D]/70 leading-relaxed">
                                    By accessing or using SAPA's spa directory platform ("Service"), you agree to be bound by these 
                                    Terms of Service. SAPA provides an online directory connecting users with spa and wellness 
                                    establishments in Bangalore. If you disagree with any part of these terms, you may not access 
                                    our Service.
                                </p>
                            </section>

                            <section className="bg-white rounded-2xl p-8 border border-[#E0DAD0]">
                                <h2 className="font-heading text-2xl text-[#4A4E4D] mb-4">2. Description of Service</h2>
                                <p className="text-[#4A4E4D]/70 leading-relaxed mb-4">
                                    SAPA is a spa directory platform that provides:
                                </p>
                                <ul className="list-disc pl-6 space-y-2 text-[#4A4E4D]/70">
                                    <li>Comprehensive listings of spas, wellness centers, and massage parlors in Bangalore</li>
                                    <li>Search and filter functionality by location, spa type, services, and ratings</li>
                                    <li>Detailed spa profiles including services, amenities, operating hours, and contact information</li>
                                    <li>User reviews and ratings to help make informed decisions</li>
                                    <li>Map-based spa discovery for location-aware browsing</li>
                                    <li>Favorites feature to save preferred spa establishments</li>
                                    <li>Contact facilitation between users and spa establishments</li>
                                </ul>
                            </section>

                            <section className="bg-white rounded-2xl p-8 border border-[#E0DAD0]">
                                <h2 className="font-heading text-2xl text-[#4A4E4D] mb-4">3. User Responsibilities</h2>
                                <h3 className="font-heading text-xl text-[#4A4E4D] mb-3 mt-6">3.1 For Spa Seekers</h3>
                                <p className="text-[#4A4E4D]/70 leading-relaxed mb-4">
                                    As a user searching for spa services, you agree to:
                                </p>
                                <ul className="list-disc pl-6 space-y-2 text-[#4A4E4D]/70">
                                    <li>Provide accurate contact information when making inquiries</li>
                                    <li>Respect spa policies regarding bookings, cancellations, and conduct</li>
                                    <li>Submit honest and fair reviews based on actual experiences</li>
                                    <li>Not use the platform for any illegal or unauthorized purposes</li>
                                    <li>Verify spa credentials independently for specialized treatments</li>
                                </ul>

                                <h3 className="font-heading text-xl text-[#4A4E4D] mb-3 mt-6">3.2 For Spa Establishments</h3>
                                <p className="text-[#4A4E4D]/70 leading-relaxed mb-4">
                                    Spa businesses listed on our platform agree to:
                                </p>
                                <ul className="list-disc pl-6 space-y-2 text-[#4A4E4D]/70">
                                    <li>Provide accurate and up-to-date business information</li>
                                    <li>Maintain valid licenses and permits required for spa operations</li>
                                    <li>Honor services and pricing as displayed on listings</li>
                                    <li>Respond professionally to user inquiries and reviews</li>
                                    <li>Comply with all applicable health, safety, and hygiene regulations</li>
                                    <li>Not engage in fraudulent or misleading advertising</li>
                                </ul>
                            </section>

                            <section className="bg-white rounded-2xl p-8 border border-[#E0DAD0]">
                                <h2 className="font-heading text-2xl text-[#4A4E4D] mb-4">4. Spa Listing Standards</h2>
                                <p className="text-[#4A4E4D]/70 leading-relaxed mb-4">
                                    All spa listings on SAPA must meet the following criteria:
                                </p>
                                <ul className="list-disc pl-6 space-y-2 text-[#4A4E4D]/70">
                                    <li>Valid business registration and operating licenses</li>
                                    <li>Physical location within Bangalore city limits</li>
                                    <li>Minimum rating threshold of 3.0 stars to maintain listing visibility</li>
                                    <li>Professional and appropriate service offerings</li>
                                    <li>Compliance with Karnataka state regulations for wellness establishments</li>
                                    <li>Trained and qualified staff for specialized treatments (Ayurveda, therapeutic massage)</li>
                                </ul>
                            </section>

                            <section className="bg-white rounded-2xl p-8 border border-[#E0DAD0]">
                                <h2 className="font-heading text-2xl text-[#4A4E4D] mb-4">5. Booking & Transactions</h2>
                                <p className="text-[#4A4E4D]/70 leading-relaxed mb-4">
                                    SAPA serves as a directory and connection platform. Please note:
                                </p>
                                <ul className="list-disc pl-6 space-y-2 text-[#4A4E4D]/70">
                                    <li>All bookings and appointments are made directly with spa establishments</li>
                                    <li>Payment for services is handled between you and the spa</li>
                                    <li>SAPA does not guarantee availability, pricing, or service quality</li>
                                    <li>Cancellation and refund policies are determined by individual spas</li>
                                    <li>We recommend confirming details directly with the spa before visiting</li>
                                </ul>
                            </section>

                            <section className="bg-white rounded-2xl p-8 border border-[#E0DAD0]">
                                <h2 className="font-heading text-2xl text-[#4A4E4D] mb-4">6. Reviews & Ratings</h2>
                                <p className="text-[#4A4E4D]/70 leading-relaxed mb-4">
                                    Our review system helps users make informed decisions:
                                </p>
                                <ul className="list-disc pl-6 space-y-2 text-[#4A4E4D]/70">
                                    <li>Reviews must be based on genuine experiences at the spa</li>
                                    <li>Fake, paid, or incentivized reviews are strictly prohibited</li>
                                    <li>Reviews containing offensive, discriminatory, or inappropriate content will be removed</li>
                                    <li>Spa establishments may respond to reviews professionally</li>
                                    <li>SAPA reserves the right to moderate and remove reviews that violate guidelines</li>
                                    <li>Rating manipulation by spas or users is grounds for removal from the platform</li>
                                </ul>
                            </section>

                            <section className="bg-white rounded-2xl p-8 border border-[#E0DAD0]">
                                <h2 className="font-heading text-2xl text-[#4A4E4D] mb-4">7. Intellectual Property</h2>
                                <p className="text-[#4A4E4D]/70 leading-relaxed">
                                    All content on SAPA, including but not limited to text, graphics, logos, spa listings data, 
                                    and software, is the property of SAPA or its content suppliers and is protected by 
                                    intellectual property laws. Spa establishments retain rights to their own branding and images 
                                    submitted for listings.
                                </p>
                            </section>

                            <section className="bg-white rounded-2xl p-8 border border-[#E0DAD0]">
                                <h2 className="font-heading text-2xl text-[#4A4E4D] mb-4">8. Disclaimer of Warranties</h2>
                                <p className="text-[#4A4E4D]/70 leading-relaxed mb-4">
                                    SAPA provides spa information "as is" without warranties of any kind:
                                </p>
                                <ul className="list-disc pl-6 space-y-2 text-[#4A4E4D]/70">
                                    <li>We do not guarantee the accuracy of spa listing information</li>
                                    <li>Service quality and outcomes are the responsibility of individual spas</li>
                                    <li>We are not liable for any health issues arising from spa treatments</li>
                                    <li>Users should verify spa credentials for medical or therapeutic treatments</li>
                                    <li>Consult healthcare providers before certain treatments if you have health conditions</li>
                                </ul>
                            </section>

                            <section className="bg-white rounded-2xl p-8 border border-[#E0DAD0]">
                                <h2 className="font-heading text-2xl text-[#4A4E4D] mb-4">9. Limitation of Liability</h2>
                                <p className="text-[#4A4E4D]/70 leading-relaxed">
                                    SAPA shall not be liable for any indirect, incidental, special, consequential, or punitive 
                                    damages resulting from your use of the Service, spa treatments received, or any disputes 
                                    between users and spa establishments. Our liability is limited to the maximum extent 
                                    permitted by applicable law.
                                </p>
                            </section>

                            <section className="bg-white rounded-2xl p-8 border border-[#E0DAD0]">
                                <h2 className="font-heading text-2xl text-[#4A4E4D] mb-4">10. Health & Safety Advisory</h2>
                                <p className="text-[#4A4E4D]/70 leading-relaxed mb-4">
                                    For your safety and wellbeing:
                                </p>
                                <ul className="list-disc pl-6 space-y-2 text-[#4A4E4D]/70">
                                    <li>Inform spa staff of any health conditions, allergies, or medications</li>
                                    <li>Pregnant women should consult doctors before certain treatments</li>
                                    <li>Verify therapist qualifications for Ayurvedic or medical spa treatments</li>
                                    <li>Report any unsafe practices or hygiene concerns to us immediately</li>
                                    <li>Trust your instincts - leave if you feel uncomfortable</li>
                                </ul>
                            </section>

                            <section className="bg-white rounded-2xl p-8 border border-[#E0DAD0]">
                                <h2 className="font-heading text-2xl text-[#4A4E4D] mb-4">11. Termination</h2>
                                <p className="text-[#4A4E4D]/70 leading-relaxed">
                                    We reserve the right to terminate or suspend access to our Service immediately, without 
                                    prior notice, for conduct that we believe violates these Terms, is harmful to other users, 
                                    spas, or third parties, or for any other reason at our sole discretion. Spa listings may 
                                    be removed for violation of listing standards or user complaints.
                                </p>
                            </section>

                            <section className="bg-white rounded-2xl p-8 border border-[#E0DAD0]">
                                <h2 className="font-heading text-2xl text-[#4A4E4D] mb-4">12. Governing Law</h2>
                                <p className="text-[#4A4E4D]/70 leading-relaxed">
                                    These Terms shall be governed by and construed in accordance with the laws of India, 
                                    specifically the state of Karnataka. Any disputes arising from these terms or use of 
                                    our Service shall be subject to the exclusive jurisdiction of the courts in Bangalore.
                                </p>
                            </section>

                            <section className="bg-white rounded-2xl p-8 border border-[#E0DAD0]">
                                <h2 className="font-heading text-2xl text-[#4A4E4D] mb-4">13. Changes to Terms</h2>
                                <p className="text-[#4A4E4D]/70 leading-relaxed">
                                    We reserve the right to modify these Terms at any time. Changes will be effective 
                                    immediately upon posting on the website. Your continued use of the Service after 
                                    changes constitutes acceptance of the modified Terms.
                                </p>
                            </section>

                            <section className="bg-white rounded-2xl p-8 border border-[#E0DAD0]">
                                <h2 className="font-heading text-2xl text-[#4A4E4D] mb-4">14. Contact Information</h2>
                                <p className="text-[#4A4E4D]/70 leading-relaxed">
                                    For questions about these Terms of Service, contact us at:
                                </p>
                                <div className="mt-4 p-4 bg-[#F9F6F0] rounded-xl">
                                    <p className="text-[#4A4E4D]"><strong>SAPA - The Spa Directory</strong></p>
                                    <p className="text-[#4A4E4D]/70">Email: info@thesapa.com</p>
                                    <p className="text-[#4A4E4D]/70">Phone: +91xxxxxxxxxx</p>
                                    <p className="text-[#4A4E4D]/70">Bangalore, Karnataka, India</p>
                                </div>
                            </section>
                        </div>

                        <div className="mt-12 text-center">
                            <Link to="/" className="btn-outline-gold">
                                Back to Home
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default TermsOfService;
